import { useCallback, useEffect, useState } from "react";
import { X, Download, ExternalLink, FileText } from "lucide-react";
import { http } from "../../lib/api";
import { downloadBlob, openBlob } from "./finDownload";
import PdfCanvasViewer from "./PdfCanvasViewer";

// Sayfa içi belge önizleme paneli (sağdan kayar): PDF → PDF.js, resim (png/jpg) → img; ESC / zemin / X kapatır; "Yeni sekmede aç" ve "İndir" kısayolları
export default function PdfPreviewPanel({ doc, onClose }) {
  const [blob, setBlob] = useState(null);
  const [err, setErr] = useState("");
  const [imgUrl, setImgUrl] = useState(null);
  const onError = useCallback((m) => setErr(m), []);
  useEffect(() => {
    if (!doc) return;
    let obj = null;
    setBlob(null); setErr(""); setImgUrl(null);
    http.get(doc.path, { responseType: "blob" })
      .then((r) => {
        const type = r.data.type || "";
        if (type.startsWith("image/")) { obj = URL.createObjectURL(r.data); setImgUrl(obj); setBlob(r.data); }
        else if (type.includes("pdf") || type === "application/octet-stream" || !type) setBlob(new Blob([r.data], { type: "application/pdf" }));
        else setErr("Bu dosya türü panelde gösterilemiyor (yalnız PDF ve resim)");
      })
      .catch(() => setErr("Evrak yüklenemedi"));
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [doc]);
  useEffect(() => {
    if (!doc) return;
    const k = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener("keydown", k, true);
    return () => window.removeEventListener("keydown", k, true);
  }, [doc, onClose]);
  if (!doc) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="pdf-preview-backdrop" data-esc-inner>
      <div className="h-full w-[min(960px,96vw)] bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pdf-preview-panel" style={{ animation: "pdfSlideIn .25s ease-out" }}>
        <style>{`@keyframes pdfSlideIn { from { transform: translateX(40px); opacity: 0 } to { transform: translateX(0); opacity: 1 } }`}</style>
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <FileText className="w-4 h-4 text-emerald-700 shrink-0" />
          <div className="min-w-0 flex-1 basis-40">
            <div className="text-xs font-bold text-slate-800 truncate" data-testid="pdf-preview-title">{doc.title || doc.name}</div>
            <div className="text-[10px] text-slate-500 truncate" data-no-upper title={doc.name}>{doc.name}</div>
          </div>
          <div className="w-full sm:w-auto sm:ml-auto flex items-center gap-1.5 shrink-0">
            <button type="button" onClick={() => openBlob(doc.path, doc.name)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 bg-white text-[11px] font-semibold hover:bg-slate-100" data-testid="pdf-preview-newtab"><ExternalLink className="w-3 h-3" /> Yeni sekmede aç</button>
            <button type="button" onClick={() => downloadBlob(doc.path, doc.name)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 bg-white text-[11px] font-semibold hover:bg-slate-100" data-testid="pdf-preview-download"><Download className="w-3 h-3" /> İndir</button>
            <button type="button" onClick={onClose} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800" data-testid="pdf-preview-close"><X className="w-3 h-3" /> Kapat <kbd className="ml-0.5 px-1 rounded border border-slate-500 text-[9px]">ESC</kbd></button>
          </div>
        </div>
        <div className="flex-1 bg-slate-100 min-h-0">
          {err && <div className="p-8 text-center text-sm text-red-700" data-testid="pdf-preview-error">{err} — <button type="button" onClick={() => openBlob(doc.path, doc.name)} className="underline">yeni sekmede açmayı deneyin</button></div>}
          {!err && !blob && <div className="p-8 text-center text-sm text-slate-500" data-testid="pdf-preview-loading">Yükleniyor…</div>}
          {!err && blob && imgUrl && <div className="h-full overflow-auto p-4 bg-slate-200/60"><img src={imgUrl} alt={doc.name} className="max-w-full mx-auto shadow-md bg-white" data-testid="pdf-preview-image" /></div>}
          {!err && blob && !imgUrl && <PdfCanvasViewer blob={blob} onError={onError} />}
        </div>
      </div>
    </div>
  );
}
